import { ExternalLink, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { CommunityLiveSessionsQuery } from '@/generated/graphql';
import { liveSessionToCopyLibrary } from '@/lib/live-watch/liveSessionToCopyLibrary';

type CommunityLiveSession = CommunityLiveSessionsQuery['communityLiveSessions'][number];

type CommunityLiveWatchCardProps = {
  session: CommunityLiveSession;
  onCurate?: (session: CommunityLiveSession) => void;
};

const PLATFORM_LABELS: Record<CommunityLiveSession['platform'], string> = {
  youtube: 'YouTube',
  twitch: 'Twitch',
  kick: 'Kick',
};

export function CommunityLiveWatchCard({ session, onCurate }: CommunityLiveWatchCardProps) {
  const { t } = useTranslation();
  const channelName = session.youtube_channel.name?.trim() || session.youtube_channel.handle?.trim();
  const canCurate = liveSessionToCopyLibrary(session) !== null;
  const mediaUrl = session.library?.thumbnail_url || session.youtube_channel.thumbnail;
  const handleCurate = () => {
    if (!canCurate) return;
    onCurate?.(session);
  };

  return (
    <Card className={`relative overflow-hidden p-0 border-red-500/30 bg-gradient-to-b from-red-500/10 via-card to-card ${
      canCurate ? 'group cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow' : ''
    }`}>
      {canCurate && (
        <button
          type="button"
          aria-label={`${t('communityLibrary.liveWatch.curate')}: ${channelName || PLATFORM_LABELS[session.platform]}`}
          className="absolute inset-0 z-10 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={handleCurate}
        />
      )}

      <div className="aspect-video bg-muted overflow-hidden relative">
        {mediaUrl ? (
          <img
            src={mediaUrl}
            alt=""
            className={`h-full w-full object-cover transition-transform duration-300 ${
              canCurate ? 'group-hover:scale-105' : ''
            }`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-red-500/15 font-semibold text-red-600 dark:text-red-400">
            {(channelName || PLATFORM_LABELS[session.platform]).charAt(0).toUpperCase()}
          </div>
        )}

        <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
          {t('communityLibrary.liveWatch.badge')}
        </div>

        <div className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-400 backdrop-blur-sm">
          {PLATFORM_LABELS[session.platform]}
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex gap-1.5 items-center px-1.5 pb-1.5 pt-6 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
          <Button
            variant="gradient"
            size="sm"
            className="relative z-20 flex-1 h-7 text-xs font-semibold"
            disabled={!canCurate}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleCurate();
            }}
          >
            <Sparkles className="w-3 h-3 mr-1" />
            {t('communityLibrary.liveWatch.curate')}
          </Button>
          {session.live_url && (
            <Button asChild variant="secondary" size="sm" className="relative z-20 h-7 shrink-0 text-xs">
              <a href={session.live_url} target="_blank" rel="noopener noreferrer">
                {t('communityLibrary.liveWatch.openLive')}
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-1">
        {channelName && (
          <p className="text-xs text-muted-foreground truncate">{channelName}</p>
        )}
        <h3 className="font-semibold text-sm">{t('communityLibrary.liveWatch.title')}</h3>
        <p className="text-xs text-muted-foreground">
          {canCurate
            ? t('communityLibrary.liveWatch.subtitle')
            : t('communityLibrary.liveWatch.waitingForClips')}
        </p>
      </div>
    </Card>
  );
}
